import React, { useState } from 'react';
import { Table, Badge, Form, Modal, Button } from 'react-bootstrap';
import { FaCopy, FaMapMarkerAlt, FaEdit, FaFileAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ClinicTable = ({ clinics, startIndex = 0, role, onToggleStatus }) => {
  const navigate = useNavigate();
  const [copiedClinicId, setCopiedClinicId] = useState(null);
  
  // Modal State
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [activeNotes, setActiveNotes] = useState('');

  const handleCopyCoords = (lat, lng, clinicId) => {
    navigator.clipboard.writeText(`${lat}, ${lng}`).then(() => {
      setCopiedClinicId(clinicId);
      setTimeout(() => setCopiedClinicId(null), 2000);
    });
  };

  const handleShowNotes = (notes) => {
    setActiveNotes(notes || 'No notes have been added for this clinic yet.');
    setShowNotesModal(true);
  };

  const handleCloseNotes = () => {
    setShowNotesModal(false);
    setActiveNotes('');
  };

  return (
    <>
      <div className="table-responsive">
        <Table striped bordered hover className="text-center align-middle">
          <thead className="table-dark align-top">
            <tr>
              <th>S. NO.</th>
              <th>CLINIC CODE</th>
              {role === 'SUPPORT' && <th>ADMIN (PARTNER)</th>}
              <th>FIELD OFFICERS</th>
              {role === 'SUPPORT' && <th>CUSTOMER TYPE</th>}
              <th>LOCATION</th>
              <th>CONTACT</th>
              <th>STATUS</th>
              <th>GPS LOCATION</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {clinics && clinics.length > 0 ? (
              clinics.map((clinic, index) => {
                const coordsText = `${clinic.latitude}, ${clinic.longitude}`;
                const hasCoords = clinic.latitude && clinic.longitude;
                const isFunctional = clinic.status === 'FUNCTIONAL';

                return (
                  <tr key={clinic.clinic_id}>
                    <td>{startIndex + index + 1}</td>
                    <td className="fw-bold text-primary">{clinic.clinic_code}</td>
                    
                    {role === 'SUPPORT' && (
                      <td className="fw-semibold">{clinic.admin_name || clinic.admin_id}</td>
                    )}
                    
                    <td style={{ maxWidth: '200px' }}>
                      {clinic.field_officers && clinic.field_officers.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1 justify-content-center">
                          {clinic.field_officers.map(fo => (
                            <Badge bg="secondary" key={fo.id} className="fw-normal">
                              {fo.name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted small">Unassigned</span>
                      )}
                    </td>

                    {role === 'SUPPORT' && (
                      <td>{clinic.customer_type || '-'}</td>
                    )}

                    <td>
                      <div>{clinic.district}</div>
                      <div className="small text-muted">{clinic.state}</div>
                    </td>
                    
                    <td>{clinic.contact || '-'}</td>
                    
                    <td style={{ width: '90px' }}>
                      {role === 'SUPPORT' ? (
                        <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                          <Form.Check 
                            type="switch"
                            id={`status-switch-${clinic.clinic_id}`}
                            checked={isFunctional}
                            onChange={() => onToggleStatus(clinic.clinic_id, clinic.status)}
                            className="cursor-pointer m-0"
                            title="Toggle Status"
                          />
                          <Badge bg={isFunctional ? 'success' : 'danger'} style={{ fontSize: '0.7em' }}>
                            {clinic.status}
                          </Badge>
                        </div>
                      ) : (
                        <Badge bg={isFunctional ? 'success' : 'danger'} style={{ fontSize: '0.75em' }}>
                          {clinic.status}
                        </Badge>
                      )}
                    </td>

                    <td style={{ minWidth: 160 }}>
                      {hasCoords ? (
                        <div style={{ fontSize: 12 }}>
                          <div style={{ fontFamily: 'monospace', marginBottom: 4 }}>
                            <div><span style={{ color: '#6b7280' }}>Lat:</span> {clinic.latitude}</div>
                            <div><span style={{ color: '#6b7280' }}>Lng:</span> {clinic.longitude}</div>
                          </div>
                          <div className="d-flex gap-1 justify-content-center">
                            <button
                              onClick={() => handleCopyCoords(clinic.latitude, clinic.longitude, clinic.clinic_id)}
                              title="Copy coordinates"
                              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11, color: copiedClinicId === clinic.clinic_id ? '#16a34a' : '#374151' }}
                            >
                              <FaCopy size={10} className="me-1" />
                              {copiedClinicId === clinic.clinic_id ? 'Copied!' : 'Copy'}
                            </button>
                            <a
                              href={`https://www.google.com/maps?q=${coordsText}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ border: '1px solid #93c5fd', borderRadius: 4, padding: '2px 6px', fontSize: 11, color: '#1d4ed8', textDecoration: 'none', background: '#eff6ff' }}
                            >
                              <FaMapMarkerAlt size={10} className="me-1" /> Maps
                            </a>
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    <td style={{ minWidth: 150 }}>
                      <div className="d-flex flex-wrap align-items-center justify-content-center gap-2">
                        <button
                          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                          onClick={() => handleShowNotes(clinic.about)}
                          title="View Clinic Notes"
                        >
                          <FaFileAlt size={14} />
                          <span>Notes</span>
                        </button>
                        
                        <button
                          className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                          onClick={() => {
                            const editRoute = `/clinics/edit/${clinic.clinic_id}`;
                            navigate(editRoute);
                          }}
                          title="Edit Clinic"
                        >
                          <FaEdit size={14} />
                          <span>Edit</span>
                        </button>
                      </div>
                    </td>

                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="py-4 text-muted" colSpan={role === 'SUPPORT' ? 10 : 8}>
                  No Clinics Found...
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* About/Notes Modal */}
      <Modal show={showNotesModal} onHide={handleCloseNotes} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Clinic Notes & About</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div 
            className="p-3 bg-light rounded border border-light" 
            style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}
          >
            {activeNotes}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseNotes}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ClinicTable;